/* eslint-disable max-len */
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import './404.scss';

// Animation variants
const containerVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.5,
      staggerChildren: 0.15,
    },
  },
};

const numberVariants = {
  initial: {
    opacity: 0,
    rotateX: -90,
    y: 50,
  },
  animate: {
    opacity: 1,
    rotateX: 0,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 200,
      damping: 20,
    },
  },
};

const textVariants = {
  initial: {
    opacity: 0,
    y: 30,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.165, 0.84, 0.44, 1],
    },
  },
};

const buttonVariants = {
  initial: {
    opacity: 0,
    y: 20,
    scale: 0.95,
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: [0.165, 0.84, 0.44, 1],
    },
  },
  hover: {
    y: -4,
    boxShadow: '0 10px 30px rgba(201, 169, 98, 0.3)',
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 20,
    },
  },
  tap: {
    scale: 0.98,
  },
};

function NotFoundPage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  return (
    <div className="not-found-container" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <motion.div
        className="content"
        variants={containerVariants}
        initial="initial"
        animate="animate"
      >
        <motion.h1 variants={numberVariants}>
          <span className="number">4</span>
          <span className="number">0</span>
          <span className="number">4</span>
        </motion.h1>

        <motion.h2 variants={textVariants}>
          {t('notFound.subtitle')}
        </motion.h2>

        <motion.p variants={textVariants}>
          {t('notFound.message')}
        </motion.p>

        <motion.div className="button-group" variants={textVariants}>
          <motion.div
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            <Link to="/" className="btn-primary">
              {t('notFound.returnHome')}
            </Link>
          </motion.div>

          <motion.div
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            <Link to="/concepts" className="btn-secondary">
              {t('notFound.viewCollections')}
            </Link>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Decorative animated elements */}
      <motion.div
        className="decorative-line"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1, delay: 0.8, ease: [0.19, 1, 0.22, 1] }}
      />
    </div>
  );
}

export default NotFoundPage;
